import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';


const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const ADMIN_QMANAGER_USERS_API = process.env.REACT_APP_ADMIN_QMANAGER_USERS_API || `${API_BASE_URL}/api/auth/manageable-users/`;

const AdminQuestionManagerPage = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({
        name: '',
        email: '',
        phone: '',
        is_active: true,
        password: '',
    });
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isFormOpen, setIsFormOpen] = useState(false);

    const headers = useMemo(() => {
        // Try guest_access_token first (for backward compatibility)
        let token = (localStorage.getItem('guest_access_token') || sessionStorage.getItem('guest_access_token') || '').trim();
        
        // If not found, try admin userDetails (from redux-based login)
        if (!token) {
            const userDetailsStr = localStorage.getItem('userDetails');
            if (userDetailsStr) {
                try {
                    const userDetails = JSON.parse(userDetailsStr);
                    token = userDetails.access || '';
                } catch (e) {
                    // JSON parse error, token remains empty
                }
            }
        }
        
        if (!token) {
            return null;
        }

        return {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        };
    }, []);

    const detailUrl = (id) => `${ADMIN_QMANAGER_USERS_API}${id}/`;

    const resetForm = () => {
        setForm({ name: '', email: '', phone: '', is_active: true, password: '' });
        setEditingId(null);
        setIsFormOpen(false);
    };

    const openAddForm = () => {
        setEditingId(null);
        setForm({ name: '', email: '', phone: '', is_active: true, password: '' });
        setErrorMessage('');
        setSuccessMessage('');
        setIsFormOpen(true);
    };

    const fetchRows = async () => {
        if (!headers) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(ADMIN_QMANAGER_USERS_API, { headers });
            const data = response.ok ? await response.json() : [];
            const filtered = (Array.isArray(data) ? data : []).filter(
                (user) => user.is_staff && !user.is_superuser,
            );
            setRows(filtered);
        } catch (error) {
            setRows([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRows();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [headers]);

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        if (errorMessage) setErrorMessage('');
        if (successMessage) setSuccessMessage('');
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrorMessage('');
        setSuccessMessage('');

        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const name = form.name.trim();
        const email = form.email.trim().toLowerCase();
        const phone = form.phone.trim();
        const password = form.password.trim();

        if (!name) {
            setErrorMessage('Question manager name is required.');
            return;
        }

        if (!email) {
            setErrorMessage('Email is required.');
            return;
        }

        if (!editingId && password.length < 8) {
            setErrorMessage('Password is required and must be at least 8 characters for new question manager.');
            return;
        }

        setSubmitting(true);
        try {
            const nameParts = name.split(/\s+/);
            const firstName = nameParts[0] || '';
            const lastName = nameParts.slice(1).join(' ');

            const payload = {
                email,
                username: email,
                first_name: firstName,
                last_name: lastName,
                phone,
                is_staff: true,
                is_superuser: false,
                is_active: !!form.is_active,
            };

            if (password) {
                payload.password = password;
            }

            const response = await fetch(editingId ? detailUrl(editingId) : ADMIN_QMANAGER_USERS_API, {
                method: editingId ? 'PATCH' : 'POST',
                headers,
                body: JSON.stringify(payload),
            });

            const data = await response.json().catch(() => ({}));

            if (!response.ok) {
                const firstError = data?.error
                    || data?.email?.[0]
                    || data?.password?.[0]
                    || 'Unable to save question manager.';
                setErrorMessage(firstError);
                return;
            }

            setSuccessMessage(editingId ? 'Question manager updated successfully.' : 'New question manager added successfully.');
            resetForm();
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEdit = (item) => {
        const fullName = `${item.first_name || ''} ${item.last_name || ''}`.trim();
        setEditingId(item.id);
        setForm({
            name: fullName,
            email: item.email || '',
            phone: item.phone || '',
            is_active: !!item.is_active,
            password: '',
        });
        setErrorMessage('');
        setSuccessMessage('Editing selected question manager. Leave password empty to keep current password.');
        setIsFormOpen(true);
    };

    const handleDelete = async (itemId) => {
        if (!headers) {
            setErrorMessage('Session expired. Please log in again.');
            return;
        }

        const result = await Swal.fire({
            title: 'Delete question manager?',
            text: 'This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete',
            cancelButtonText: 'Cancel',
        });
        if (!result.isConfirmed) return;

        setSubmitting(true);
        setErrorMessage('');
        setSuccessMessage('');

        try {
            const response = await fetch(detailUrl(itemId), {
                method: 'DELETE',
                headers,
            });

            if (!response.ok) {
                const data = await response.json().catch(() => ({}));
                setErrorMessage(data?.error || 'Unable to delete question manager.');
                return;
            }

            if (editingId === itemId) {
                resetForm();
            }

            setSuccessMessage('Question manager deleted successfully.');
            await fetchRows();
        } catch (error) {
            setErrorMessage('Unable to reach server right now.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="admin-panel-card admin-table-section">
            {isFormOpen ? (
                <div className="admin-top-sheet-backdrop" onClick={resetForm}>
                    <form
                        className="admin-college-form admin-top-sheet"
                        onSubmit={handleSubmit}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="admin-top-sheet-header">
                            <h4 className="admin-college-dropdown-title">{editingId ? 'Edit Question Manager' : 'Add New Question Manager'}</h4>
                            <button
                                type="button"
                                className="admin-top-sheet-close"
                                onClick={resetForm}
                                disabled={submitting}
                                aria-label="Close question manager form"
                            >
                                x
                            </button>
                        </div>

                        <div className="admin-college-form-grid">
                            <div>
                                <label className="admin-security-label" htmlFor="name">Name</label>
                                <input
                                    id="name"
                                    name="name"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.name}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="email">Email</label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="admin-security-input"
                                    value={form.email}
                                    onChange={handleInputChange}
                                    disabled={submitting || !!editingId}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="phone">Phone</label>
                                <input
                                    id="phone"
                                    name="phone"
                                    type="text"
                                    className="admin-security-input"
                                    value={form.phone}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="is_active">Status</label>
                                <select
                                    id="is_active"
                                    name="is_active"
                                    className="admin-security-input"
                                    value={form.is_active ? 'active' : 'pending'}
                                    onChange={(event) =>
                                        setForm((prev) => ({
                                            ...prev,
                                            is_active: event.target.value === 'active',
                                        }))
                                    }
                                    disabled={submitting}
                                >
                                    <option value="active">Active</option>
                                    <option value="pending">Pending</option>
                                </select>
                            </div>
                            <div>
                                <label className="admin-security-label" htmlFor="password">
                                    {editingId ? 'New Password (Optional)' : 'Password'}
                                </label>
                                <input
                                    id="password"
                                    name="password"
                                    type="password"
                                    className="admin-security-input"
                                    value={form.password}
                                    onChange={handleInputChange}
                                    disabled={submitting}
                                />
                            </div>
                        </div>

                        <div className="admin-college-actions">
                            <button type="submit" className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn" disabled={submitting}>
                                {submitting ? 'Saving...' : editingId ? 'Update Question Manager' : 'Add Question Manager'}
                            </button>
                            <button
                                type="button"
                                className="admin-profile-action-btn"
                                onClick={resetForm}
                                disabled={submitting}
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                </div>
            ) : null}

            <div className="admin-college-header">
                <div>
                    <h3>Question Manager</h3>
                    <p className="admin-subtitle">Manage question manager accounts with add, edit, and delete options.</p>
                </div>
                <button
                    type="button"
                    className="admin-profile-action-btn admin-profile-action-btn-primary admin-college-primary-btn"
                    onClick={openAddForm}
                    disabled={submitting}
                >
                    Add Question Manager
                </button>
            </div>

            {errorMessage ? <div className="admin-security-feedback admin-security-feedback-error">{errorMessage}</div> : null}
            {successMessage ? <div className="admin-security-feedback admin-security-feedback-success">{successMessage}</div> : null}

            <div className="admin-table-wrap">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Phone</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="6">Loading question managers...</td></tr>
                        ) : rows.length === 0 ? (
                            <tr><td colSpan="6">No question managers found.</td></tr>
                        ) : (
                            rows.map((item) => (
                                <tr key={item.id}>
                                    <td>{item.id}</td>
                                    <td>{`${item.first_name || ''} ${item.last_name || ''}`.trim() || '-'}</td>
                                    <td>{item.email || '-'}</td>
                                    <td>{item.phone || '-'}</td>
                                    <td>{item.is_active ? 'Active' : 'Pending'}</td>
                                    <td>
                                        <div className="admin-row-actions">
                                            <button
                                                type="button"
                                                className="admin-row-action-btn"
                                                onClick={() => handleEdit(item)}
                                                disabled={submitting}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="admin-row-action-btn admin-row-action-btn-danger"
                                                onClick={() => handleDelete(item.id)}
                                                disabled={submitting}
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminQuestionManagerPage;
